import { useEffect, useRef, useState } from "react";
import { Instagram, AlertCircle } from "lucide-react";
import { useInstagramFeed } from "@/hooks/useInstagramFeed";

const FEED_ID = "7ESTTWDjt4ePAPRS6cZD";
const MAX_POSTS = 4;

export function InstagramFeed() {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  // Lazy-load: só busca o feed quando o Footer estiver perto da viewport.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const { posts, loading, error } = useInstagramFeed(FEED_ID, MAX_POSTS, visible);

  useEffect(() => {
    if (loading && visible) setAnnouncement("Carregando feed do Instagram...");
    else if (error) setAnnouncement("Feed do Instagram indisponível.");
    else if (posts.length > 0) setAnnouncement(`${posts.length} posts do Instagram carregados.`);
    else setAnnouncement("");
  }, [loading, error, posts.length, visible]);

  return (
    <section
      aria-label="Posts recentes do Instagram do Atacado Óptico"
      className="bg-primary/95 border-t border-primary-foreground/10"
    >
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <div ref={sentinelRef} aria-hidden="true" />
      <div className="container py-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Instagram className="h-5 w-5 text-accent" aria-hidden="true" />
            <h3 className="font-heading font-semibold text-primary-foreground">
              Siga no Instagram
            </h3>
          </div>
          <a
            href="https://instagram.com/atacado_optico"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-accent hover:underline inline-flex items-center min-h-11 py-2"
            aria-label="Abrir perfil @atacado_optico no Instagram"
          >
            @atacado_optico
          </a>
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center min-h-[200px]">
            <AlertCircle className="h-5 w-5 text-primary-foreground/30" aria-hidden="true" />
            <p className="text-sm text-primary-foreground/50 max-w-xs">
              Feed do Instagram indisponível. Veja nossas publicações diretamente no perfil.
            </p>
            <a
              href="https://instagram.com/atacado_optico"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent border border-accent/30 hover:border-accent px-4 py-2 rounded-full transition-colors hover:bg-accent/10"
            >
              <Instagram className="h-4 w-4" aria-hidden="true" />
              Abrir Instagram
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-lg bg-primary-foreground/10 animate-pulse"
                    aria-hidden="true"
                  />
                ))
              : posts.map((post) => (
                  <a
                    key={post.id}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative aspect-square overflow-hidden rounded-lg group block bg-primary-foreground/10"
                    aria-label={post.caption || "Ver publicação no Instagram do Atacado Óptico"}
                  >
                    <img
                      src={post.imageUrl}
                      alt={post.caption || "Publicação do Instagram do Atacado Óptico"}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {post.isVideo && (
                      <span
                        className="absolute top-1.5 right-1.5 text-primary-foreground drop-shadow"
                        aria-hidden="true"
                      >
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    )}
                  </a>
                ))}
          </div>
        )}
      </div>
    </section>
  );
}
